import { motion } from "framer-motion";
import { FileSignature, Scale, MessagesSquare, Stamp } from "lucide-react";
import { ChapterLabel } from "@/components/Expertise";
import { scrollToSection } from "@/components/Navbar";
import { useLanguage } from "@/i18n/LanguageContext";

const ICONS = [Stamp, Scale, MessagesSquare, FileSignature];

export default function Services() {
  const { t } = useLanguage();
  const items = t("services.items");

  return (
    <section id="services" data-testid="services-section" className="bg-[#F1EFE9] py-24 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <ChapterLabel num="02" title={t("services.label")} />
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-ink max-w-3xl mb-16"
        >
          {t("services.title")}
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
          {items.map((s, i) => {
            const Icon = ICONS[i];
            return (
              <motion.article
                key={s.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="group bg-[#F8F7F4] p-10 lg:p-12 hover:bg-slate2 transition-colors duration-500"
              >
                <Icon size={26} strokeWidth={1.25} className="text-brass mb-8" />
                <h3 className="font-serif text-2xl lg:text-3xl text-ink group-hover:text-paper transition-colors duration-500 mb-4">{s.title}</h3>
                <p className="text-sm leading-relaxed text-ink/60 group-hover:text-paper/60 transition-colors duration-500 max-w-md">{s.text}</p>
                <button
                  data-testid={`service-cta-${i}`}
                  onClick={() => scrollToSection("devis")}
                  className="mt-8 text-[11px] uppercase tracking-[0.25em] text-brass group-hover:text-brasslight transition-colors duration-500"
                >
                  {t("services.cta")}
                </button>
              </motion.article>
            );
          })}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8 }}
          data-testid="service-notes"
          className="mt-16 grid md:grid-cols-2 gap-px bg-ink/10 border border-ink/10"
        >
          {[
            { title: t("serviceNotes.shippingTitle"), points: t("serviceNotes.shippingPoints") },
            { title: t("serviceNotes.paymentTitle"), points: t("serviceNotes.paymentPoints") },
          ].map((block, i) => (
            <div key={i} data-testid={`service-note-${i}`} className="bg-[#F8F7F4] p-10 lg:p-12">
              <h3 className="font-serif text-2xl text-ink mb-7">{block.title}</h3>
              <ul className="space-y-4">
                {block.points.map((p, j) => (
                  <li key={j} className="flex gap-3 text-sm leading-relaxed text-ink/60">
                    <span className="w-1.5 h-1.5 bg-brass rotate-45 shrink-0 mt-[7px]" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
